import React from 'react';
import { Button, Modal, message as AntMessage } from 'antd';
import * as XLSX from 'xlsx';
import { errorHandle } from '../../services';
import { UploadButton } from '../upload-button';
import { downloadScheduleTemplate, getScheduleTemplateType } from './api';
import { getTemplateUploadProps } from './ban-on-utils';

type DimType = 'SPU' | 'SKU';

type ScheduleImportModalProps = {
    visible: boolean;
    dim: DimType;
    onCancel: () => void;
    onSuccess?: () => void;
};

/** 2MB，maxSize 单位为 KB */
const MAX_FILE_SIZE_KB = 2 * 1024;
/** 数据行上限（不含表头） */
const MAX_DATA_ROWS = 2000;

const countExcelDataRows = (file: File): Promise<number> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const workbook = XLSX.read(event.target?.result, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                if (!firstSheetName) {
                    reject(new Error('empty'));
                    return;
                }
                const sheet = workbook.Sheets[firstSheetName];
                const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                resolve(rows.length);
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(reader.error || new Error('read'));
        reader.readAsArrayBuffer(file);
    });

/** 仅定时上下架导入弹窗使用：增加 Excel 后缀/大小/行数校验，不改共用组件 */
class ScheduleImportExcelUploadButton extends UploadButton {
    // antd3 支持 Promise；基类类型仅声明 boolean，此处断言避免改共用组件
    beforeUpload = ((file: File) => {
        const fileName = (file.name || '').toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
        if (!isExcel) {
            AntMessage.error('仅支持.xlsx/.xls文件');
            return false;
        }
        const { maxSize } = this.props;
        if (maxSize && file.size > maxSize * 1024) {
            AntMessage.error('上传文件大小不能超过 2MB');
            return false;
        }
        if (this.state.loading) {
            return false;
        }
        return countExcelDataRows(file)
            .then((rowCount) => {
                if (rowCount > MAX_DATA_ROWS) {
                    AntMessage.error(`文件数据不能超过${MAX_DATA_ROWS}行`);
                    // antd3：Promise resolve 会继续上传，须 reject 才能阻断
                    return Promise.reject(new Error('exceed_max_rows'));
                }
                return true;
            })
            .catch((err) => {
                if (err?.message !== 'exceed_max_rows') {
                    AntMessage.error('文件解析失败，请检查文件格式');
                }
                return Promise.reject(err);
            });
    }) as UploadButton['beforeUpload'];
}

export const ScheduleImportModal = ({
    visible,
    dim,
    onCancel,
    onSuccess,
}: ScheduleImportModalProps) => {
    const templateType = getScheduleTemplateType(dim);
    const codeLabel = dim === 'SPU' ? 'SPU编码' : 'SKU编码';

    const downloadTemplate = () => {
        downloadScheduleTemplate(templateType).catch(errorHandle);
    };

    const handleUploadSuccess = () => {
        onSuccess?.();
        onCancel();
    };

    return (
        <Modal
            title={`批量导入定时上下架（${dim}）`}
            visible={visible}
            onCancel={onCancel}
            destroyOnClose
            maskClosable={false}
            width={640}
            wrapClassName="shelf-rule-import-modal"
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    关闭
                </Button>,
            ]}
        >
            <p className="shelf-rule-import-modal__tip">
                请下载 {dim} 模板，按「定时任务名称 + {codeLabel} + 上架时间 + 下架时间 +
                说明」填写后上传。品牌取自顶栏工作品牌；同名称、同时间、同说明的多行会合并为一条任务。无效行会标红。
            </p>
            <div className="shelf-rule-import-modal__actions">
                <Button onClick={downloadTemplate}>下载{dim}模板</Button>
                <ScheduleImportExcelUploadButton
                    key={templateType}
                    accept=".xlsx,.xls"
                    action={`/templates/${templateType}`}
                    buttons={{ text: '上传 Excel', type: 'primary' }}
                    style={{ display: 'inline-block' }}
                    failTitle="导入失败"
                    maxSize={MAX_FILE_SIZE_KB}
                    successTitle="导入成功，请稍后查看"
                    successContent="请在「报表管理 - 批量导入管理」中查看处理结果。"
                    successBehavior={handleUploadSuccess}
                    {...getTemplateUploadProps()}
                />
            </div>
        </Modal>
    );
};
